
import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { z } from "zod";
import { ScanFormData } from '@/hooks/useScanResultSubmission';
import { HoneypotField } from "@/components/ui/honeypot-field";

// Form schema for contact information
export const formSchema = z.object({
  name: z.string().min(2, { message: "Naam moet minstens 2 tekens bevatten." }),
  company: z.string().min(2, { message: "Bedrijfsnaam moet minstens 2 tekens bevatten." }),
  email: z.string().email({ message: "Vul een geldig e-mailadres in." }),
  website: z.string().optional(), // Honeypot field
});

interface ScanContactFormProps {
  form: UseFormReturn<ScanFormData>;
  onSubmit: (data: ScanFormData) => void;
}

const ScanContactForm: React.FC<ScanContactFormProps> = ({ form, onSubmit }) => {
  return (
    <section className="py-8 bg-gradient-to-b from-white to-gray-50">
      <div className="container max-w-2xl pt-8">
        <Card className="border-none shadow-xl animate-fade-in">
          <CardHeader className="text-center">
            <div className="mx-auto p-3 bg-brand-lightBlue rounded-full w-16 h-16 flex items-center justify-center mb-4">
              <Check className="h-8 w-8 text-brand-blue" />
            </div>
            <CardTitle className="text-3xl font-bold text-gray-900">Bijna klaar!</CardTitle>
            <CardDescription className="text-gray-700 text-lg">
              Vul je gegevens in om je persoonlijke scanresultaat te ontvangen.
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Honeypot field - invisible to real users */}
                <HoneypotField form={form} />
                
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Naam contactpersoon</FormLabel>
                      <FormControl>
                        <Input placeholder="Uw naam" {...field} className="h-12" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bedrijfsnaam</FormLabel>
                      <FormControl>
                        <Input placeholder="Uw bedrijf" {...field} className="h-12" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-mailadres</FormLabel>
                      <FormControl>
                        <Input placeholder="uw.email@bedrijf.be" {...field} className="h-12" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button type="submit" size="lg" className="w-full">
                  Ontvang mijn scanresultaat
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ScanContactForm;
